import { renderTemplateContent } from "../contentRenderer";
import { mergeTemplateDesign, templateDesignToCssVars } from "../design";
import { DefaultTemplateLink } from "../templateLink";
import type {
    ResolvedTemplateDesign,
    SiteTemplateDefinition,
    TemplateLinkComponent,
    TemplateRenderProps,
} from "../types";
import { WebBuilderPageRegion } from "../WebBuilderPageRegion";
import { WebGlobalBuilderRegion } from "../WebGlobalBuilderRegion";
import { webSystemTemplateDesignDefaults } from "../webSystemTemplateDesign";
import { MeridianAdvisoryMobileMenu } from "./MeridianAdvisoryMobileMenu";

function MeridianAdvisoryStyles() {
    return (
        <style>{`
            [data-web-template="meridian-advisory"] .site-builder-section {
                padding-inline: clamp(1rem, 3vw, 2.5rem);
                position: relative;
            }

            [data-web-template="meridian-advisory"] .site-builder-grid {
                display: grid;
                gap: clamp(1rem, 2.4vw, 2.25rem);
                margin-inline: auto;
                width: min(100%, var(--site-container));
            }

            [data-web-template="meridian-advisory"] .site-builder-grid:not(.site-builder-grid-placed) {
                grid-template-columns: repeat(12, minmax(0, 1fr));
            }

            [data-web-template="meridian-advisory"] .site-builder-grid:not(.site-builder-grid-placed) > .site-builder-grid-item {
                grid-column: 1 / -1;
            }

            [data-web-template="meridian-advisory"] .site-builder-column {
                align-content: start;
                display: grid;
                gap: 1.1rem;
                min-width: 0;
            }

            [data-web-template="meridian-advisory"] .site-builder-column-inline {
                align-items: center;
                display: flex;
                flex-wrap: wrap;
            }

            [data-web-template="meridian-advisory"] .site-builder-navigation {
                align-items: center;
                display: flex;
                flex-wrap: wrap;
                gap: clamp(1rem, 2.3vw, 2.15rem);
                justify-content: center;
            }

            [data-web-template="meridian-advisory"] .site-builder-navigation-item,
            [data-web-template="meridian-advisory"] .site-builder-navigation-link {
                display: inline-flex;
            }

            [data-web-template="meridian-advisory"] [data-web-region="header"] {
                position: sticky;
                top: 0;
                z-index: 60;
            }

            [data-web-template="meridian-advisory"] [data-web-region="header"] > .site-builder-grid {
                align-items: center;
            }

            [data-web-template="meridian-advisory"] [data-document-role="headerNavigation"] .site-builder-text {
                display: inline-flex;
                font-size: .76rem;
                font-weight: 760;
                letter-spacing: .025em;
                position: relative;
                width: auto !important;
            }

            [data-web-template="meridian-advisory"] [data-document-role="headerNavigation"] .site-builder-text::after {
                background: var(--site-accent);
                bottom: -.45rem;
                content: "";
                height: 2px;
                left: 0;
                position: absolute;
                transform: scaleX(0);
                transform-origin: left;
                transition: transform 180ms ease;
                width: 100%;
            }

            [data-web-template="meridian-advisory"] [data-document-role="headerNavigation"] .site-builder-text:hover::after,
            [data-web-template="meridian-advisory"] [data-document-role="headerNavigation"] .site-builder-text:focus-visible::after {
                transform: scaleX(1);
            }

            [data-web-template="meridian-advisory"] [data-document-role="headerActions"] {
                flex-wrap: nowrap;
                justify-content: flex-end;
            }

            [data-web-template="meridian-advisory"] [data-document-role="headerCta"],
            [data-web-template="meridian-advisory"] [data-document-role="localeActions"] {
                flex: 0 0 auto;
            }

            [data-web-template="meridian-advisory"] [data-document-role="heroCopy"] {
                column-gap: .75rem;
                display: grid;
                grid-template-columns: repeat(12, minmax(0, 1fr));
                row-gap: 1.2rem;
            }

            [data-web-template="meridian-advisory"] [data-document-role="heroCopy"] > :not([data-document-role="heroPrimaryAction"], [data-document-role="heroSecondaryAction"]) {
                grid-column: 1 / -1;
            }

            [data-web-template="meridian-advisory"] [data-document-role="heroCopy"] > [data-document-role="heroPrimaryAction"] {
                grid-column: 1 / span 6;
            }

            [data-web-template="meridian-advisory"] [data-document-role="heroCopy"] > [data-document-role="heroSecondaryAction"] {
                grid-column: 7 / span 6;
            }

            [data-web-template="meridian-advisory"] [data-document-role="heroVisual"],
            [data-web-template="meridian-advisory"] [data-document-role="editorialVisual"],
            [data-web-template="meridian-advisory"] [data-document-role="wideEditorialVisual"] {
                position: relative;
            }

            [data-web-template="meridian-advisory"] [data-document-role="heroVisual"] .site-builder-image,
            [data-web-template="meridian-advisory"] [data-document-role="editorialVisual"] .site-builder-image,
            [data-web-template="meridian-advisory"] [data-document-role="wideEditorialVisual"] .site-builder-image {
                min-height: 100%;
            }

            [data-web-template="meridian-advisory"] [data-document-role="heroFloatingNote"] {
                bottom: clamp(1rem, 3vw, 2rem);
                left: clamp(-1.5rem, -2vw, -.75rem);
                position: absolute;
                z-index: 2;
            }

            [data-web-template="meridian-advisory"] [data-web-region="homeProof"] > .site-builder-grid > .site-builder-grid-item:not(:last-child) {
                border-right: 1px solid color-mix(in srgb, var(--site-primary) 24%, transparent);
                padding-right: clamp(.8rem, 2vw, 1.75rem);
            }

            [data-web-template="meridian-advisory"] [data-web-region="footer"] a,
            [data-web-template="meridian-advisory"] [data-web-region="contactClosingCta"] a {
                text-underline-offset: .25em;
            }

            [data-web-template="meridian-advisory"] .meridian-mobile-menu-toggle {
                display: none;
            }

            [data-web-template="meridian-advisory"] {
                --meridian-accent: #B98A3D;
                --meridian-dark: #17212A;
                --meridian-ink: #111820;
                --meridian-light: #FFFCF5;
                --meridian-muted: #5E625F;
                --meridian-paper: #F1EEE6;
                --meridian-rule: #C9C1B4;
                background:
                    linear-gradient(rgba(23,33,42,.025) 1px, transparent 1px),
                    var(--site-background);
                background-size: 100% 64px, auto;
                isolation: isolate;
                scroll-behavior: smooth;
                scroll-padding-top: 112px;
            }

            [data-web-template="meridian-advisory"] ::selection {
                background: var(--meridian-accent);
                color: var(--meridian-ink);
            }

            [data-web-template="meridian-advisory"] :focus-visible {
                outline: 3px solid var(--site-accent);
                outline-offset: 4px;
            }

            [data-web-template="meridian-advisory"] .site-builder-text-h1,
            [data-web-template="meridian-advisory"] .site-builder-text-h2 {
                font-stretch: condensed;
                font-weight: 850 !important;
                text-wrap: balance;
                text-transform: uppercase;
            }

            [data-web-template="meridian-advisory"] .site-builder-text-h1 {
                line-height: .82 !important;
            }

            [data-web-template="meridian-advisory"] .site-builder-text-h2 {
                line-height: .9 !important;
            }

            [data-web-template="meridian-advisory"] .site-builder-button {
                letter-spacing: .1em;
                text-transform: uppercase;
                transition:
                    background-color 180ms ease,
                    border-color 180ms ease,
                    box-shadow 220ms ease,
                    color 180ms ease,
                    transform 220ms cubic-bezier(.2,.75,.25,1);
            }

            [data-web-template="meridian-advisory"] .site-builder-button:hover {
                box-shadow: 6px 6px 0 color-mix(in srgb, var(--site-accent) 36%, transparent);
                transform: translate(-2px, -2px);
            }

            [data-web-template="meridian-advisory"] .site-builder-card {
                box-shadow: 0 0 0 transparent;
                transition:
                    border-color 220ms ease,
                    box-shadow 260ms ease,
                    transform 260ms cubic-bezier(.2,.75,.25,1);
            }

            [data-web-template="meridian-advisory"] .site-builder-card:hover {
                border-color: var(--site-accent) !important;
                box-shadow: 8px 10px 0 color-mix(in srgb, var(--site-accent) 24%, transparent);
                transform: translateY(-4px);
            }

            [data-web-template="meridian-advisory"] [data-web-region="meridianServiceStrip"] {
                color: var(--meridian-light);
                left: 0;
                padding-inline: clamp(1rem, 3vw, 2.5rem);
                position: sticky;
                right: 0;
                top: 0;
                z-index: 72;
            }

            [data-web-template="meridian-advisory"] [data-web-region="meridianServiceStrip"] > .site-builder-grid {
                align-items: center;
            }

            [data-web-template="meridian-advisory"] [data-web-region="header"] {
                color: var(--meridian-ink);
                top: 36px;
                transition:
                    background-color 220ms ease,
                    box-shadow 220ms ease,
                    min-height 220ms ease,
                    padding 220ms ease;
                z-index: 70;
            }

            [data-web-template="meridian-advisory"] [data-document-role="headerNavigation"] .site-navigation-root {
                --site-navigation-accent: var(--meridian-dark) !important;
                --site-navigation-accentText: var(--meridian-light) !important;
                --site-navigation-background: var(--meridian-light) !important;
                --site-navigation-border: var(--meridian-rule) !important;
                --site-navigation-muted: var(--meridian-muted) !important;
                --site-navigation-surface: var(--meridian-paper) !important;
                --site-navigation-text: var(--meridian-ink) !important;
            }

            [data-web-template="meridian-advisory"] [data-document-role="headerNavigation"] .site-builder-text,
            [data-web-template="meridian-advisory"] [data-document-role="headerNavigation"] .site-builder-navigation-link {
                color: var(--meridian-ink) !important;
            }

            [data-web-template="meridian-advisory"] [data-web-region="header"][data-meridian-header-scrolled] {
                box-shadow: 0 18px 42px rgba(17,24,32,.14);
                min-height: 64px !important;
                padding-block: 8px !important;
            }

            [data-web-template="meridian-advisory"] [data-document-role="headerNavigation"] .site-builder-text,
            [data-web-template="meridian-advisory"] [data-document-role="headerNavigation"] .site-builder-navigation-link {
                font-size: .7rem;
                font-weight: 850;
                letter-spacing: .1em;
                text-transform: uppercase;
            }

            [data-web-template="meridian-advisory"] [data-web-region="homeHero"] {
                overflow: hidden;
                padding-inline: clamp(1rem, 3vw, 2.5rem);
            }

            [data-web-template="meridian-advisory"] [data-web-region="homeHero"] > .site-builder-grid {
                align-items: stretch;
                gap: 0;
            }

            [data-web-template="meridian-advisory"] [data-web-region="homeHero"] [data-document-role="heroCopy"] {
                align-content: center;
                min-height: min(820px, calc(100svh - 112px));
                position: relative;
                z-index: 2;
            }

            [data-web-template="meridian-advisory"] [data-web-region="homeHero"] [data-document-role="heroCopy"]::after {
                background: var(--site-accent);
                bottom: clamp(2rem, 5vw, 5rem);
                content: "";
                height: 3px;
                position: absolute;
                right: clamp(1.5rem, 4vw, 4rem);
                width: clamp(54px, 7vw, 96px);
            }

            [data-web-template="meridian-advisory"] [data-document-role="heroVisual"] {
                isolation: isolate;
                overflow: hidden;
            }

            [data-web-template="meridian-advisory"] [data-document-role="heroVisual"]::before {
                background: var(--site-primary);
                clip-path: polygon(0 0, 100% 0, 0 100%);
                content: "";
                height: 100%;
                left: -1px;
                pointer-events: none;
                position: absolute;
                top: 0;
                width: clamp(42px, 5vw, 76px);
                z-index: 2;
            }

            [data-web-template="meridian-advisory"] [data-document-role="heroVisual"] .site-builder-image {
                filter: grayscale(.82) contrast(1.06);
                transform: scale(1.015);
                transition: filter 700ms ease, transform 1100ms cubic-bezier(.2,.75,.25,1);
            }

            [data-web-template="meridian-advisory"] [data-document-role="heroVisual"]:hover .site-builder-image {
                filter: grayscale(0) contrast(1.03);
                transform: scale(1.05);
            }

            [data-web-template="meridian-advisory"] [data-document-role="editorialVisual"],
            [data-web-template="meridian-advisory"] [data-document-role="wideEditorialVisual"] {
                overflow: hidden;
            }

            [data-web-template="meridian-advisory"] [data-document-role="editorialVisual"] .site-builder-image,
            [data-web-template="meridian-advisory"] [data-document-role="wideEditorialVisual"] .site-builder-image {
                filter: grayscale(.7) contrast(1.04);
                transform: scale(1.01);
                transition: filter 650ms ease, transform 900ms cubic-bezier(.2,.75,.25,1);
            }

            [data-web-template="meridian-advisory"] [data-document-role="editorialVisual"]:hover .site-builder-image,
            [data-web-template="meridian-advisory"] [data-document-role="wideEditorialVisual"]:hover .site-builder-image {
                filter: grayscale(0) contrast(1.02);
                transform: scale(1.04);
            }

            [data-web-template="meridian-advisory"] [data-document-role="heroFloatingNote"] {
                border-width: 0 !important;
                box-shadow: 8px 8px 0 rgba(17,24,32,.26);
                left: clamp(1rem, 2.2vw, 2rem);
            }

            [data-web-template="meridian-advisory"] [data-web-region="homeProof"] {
                border-block: 1px solid color-mix(in srgb, var(--site-primary) 20%, transparent);
            }

            [data-web-template="meridian-advisory"] [data-web-region="homeProof"] .site-builder-text-h3 {
                text-transform: uppercase;
            }

            [data-web-template="meridian-advisory"] [data-web-region="homeServices"] .site-builder-card,
            [data-web-template="meridian-advisory"] [data-web-region="servicesOverview"] .site-builder-card,
            [data-web-template="meridian-advisory"] [data-web-region="insightsArticles"] .site-builder-card {
                border-width: 3px 0 0 0 !important;
                min-height: 100%;
            }

            [data-web-template="meridian-advisory"] [data-web-region$="Hero"]:not([data-web-region="homeHero"]),
            [data-web-template="meridian-advisory"] [data-web-region="connectHero"] {
                background-image:
                    linear-gradient(115deg, transparent 0 70%, color-mix(in srgb, var(--site-accent) 12%, transparent) 70% 70.4%, transparent 70.4%),
                    linear-gradient(rgba(255,255,255,.025) 1px, transparent 1px);
                background-size: auto, 100% 32px;
                border-bottom: 4px solid var(--site-accent);
            }

            [data-web-template="meridian-advisory"] [data-web-region$="Cta"],
            [data-web-template="meridian-advisory"] [data-web-region="homeContactCta"],
            [data-web-template="meridian-advisory"] [data-web-region="contactClosingCta"] {
                border-top: 4px solid var(--site-accent);
            }

            [data-web-template="meridian-advisory"] :where(
                [data-web-region="homeHero"],
                [data-web-region$="Hero"],
                [data-web-region$="Cta"],
                [data-web-region="homeContactCta"],
                [data-web-region="contactClosingCta"],
                [data-web-region="footer"]
            ) {
                color: var(--meridian-light);
            }

            [data-web-template="meridian-advisory"] .site-builder-form {
                color: var(--meridian-ink);
            }

            [data-web-template="meridian-advisory"] .site-builder-form :where(
                .site-builder-form-title,
                .site-builder-form-text,
                .site-builder-form-field,
                .site-template-lead-form label
            ) {
                color: var(--meridian-ink) !important;
            }

            [data-web-template="meridian-advisory"] .site-builder-form :where(
                .site-builder-form-field input,
                .site-builder-form-field select,
                .site-builder-form-field textarea,
                .site-template-lead-control
            ) {
                -webkit-text-fill-color: var(--meridian-ink);
                background: var(--meridian-light) !important;
                border-color: var(--meridian-rule) !important;
                color: var(--meridian-ink) !important;
                opacity: 1;
            }

            [data-web-template="meridian-advisory"] .site-builder-form :where(
                .site-builder-form-field input,
                .site-builder-form-field textarea,
                .site-template-lead-control
            )::placeholder {
                -webkit-text-fill-color: var(--meridian-muted);
                color: var(--meridian-muted) !important;
                opacity: 1;
            }

            [data-web-template="meridian-advisory"] .site-builder-form :where(select, option) {
                background: var(--meridian-light);
                color: var(--meridian-ink);
            }

            [data-web-template="meridian-advisory"] .site-builder-form .site-template-lead-empty-state {
                background: var(--meridian-paper) !important;
                border-color: var(--meridian-rule) !important;
                color: var(--meridian-muted) !important;
            }

            [data-web-template="meridian-advisory"] .site-builder-form :where(
                .site-builder-form-submit .site-builder-button,
                .site-builder-form-submit-preview .site-builder-button,
                .site-template-lead-submit
            ) {
                background: var(--meridian-dark) !important;
                border-color: var(--meridian-dark) !important;
                color: var(--meridian-light) !important;
            }

            [data-web-template="meridian-advisory"] [data-web-region="footer"] {
                border-top: 1px solid color-mix(in srgb, var(--site-accent) 54%, transparent);
                position: relative;
            }

            [data-web-template="meridian-advisory"] [data-web-region="footer"]::before {
                background: var(--site-accent);
                content: "";
                height: 6px;
                left: clamp(1rem, 3vw, 2.5rem);
                position: absolute;
                top: 0;
                width: clamp(70px, 10vw, 150px);
            }

            [data-web-template="meridian-advisory"]:not([data-web-template-editor="true"])[data-meridian-runtime-ready] [data-meridian-reveal] {
                opacity: 0;
                transform: translateY(26px);
                transition:
                    opacity 620ms cubic-bezier(.2,.75,.25,1),
                    transform 700ms cubic-bezier(.2,.75,.25,1);
            }

            [data-web-template="meridian-advisory"]:not([data-web-template-editor="true"])[data-meridian-runtime-ready] [data-meridian-reveal][data-meridian-revealed] {
                opacity: 1;
                transform: translateY(0);
            }

            @container (max-width: 900px) {
                [data-web-template="meridian-advisory"] [data-web-region="meridianServiceStrip"] {
                    display: none;
                }

                [data-web-template="meridian-advisory"] [data-web-region="header"] {
                    top: 0;
                }

                [data-web-template="meridian-advisory"] .site-builder-grid[data-site-builder-stack-on-mobile="true"] {
                    grid-auto-rows: auto !important;
                    grid-template-columns: minmax(0, 1fr) !important;
                }

                [data-web-template="meridian-advisory"] .site-builder-grid[data-site-builder-stack-on-mobile="true"] > .site-builder-grid-item {
                    grid-column: 1 !important;
                    grid-row: auto !important;
                }

                [data-web-template="meridian-advisory"] [data-web-region="homeProof"] > .site-builder-grid {
                    grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
                }

                [data-web-template="meridian-advisory"] [data-web-region="homeProof"] > .site-builder-grid > .site-builder-grid-item {
                    border-right: 0;
                    grid-column: auto !important;
                    grid-row: auto !important;
                    padding-right: 0;
                }

                [data-web-template="meridian-advisory"] [data-document-role="heroCopy"] {
                    grid-template-columns: minmax(0, 1fr);
                }

                [data-web-template="meridian-advisory"] [data-web-region="homeHero"] > .site-builder-grid {
                    gap: 0;
                }

                [data-web-template="meridian-advisory"] [data-web-region="homeHero"] [data-document-role="heroCopy"] {
                    min-height: auto;
                    padding: clamp(48px, 11vw, 76px) clamp(24px, 7vw, 52px) !important;
                }

                [data-web-template="meridian-advisory"] [data-web-region="homeHero"] [data-document-role="heroCopy"]::after {
                    bottom: 2rem;
                    right: 2rem;
                }

                [data-web-template="meridian-advisory"] [data-document-role="heroCopy"] > * {
                    grid-column: 1 !important;
                }

                [data-web-template="meridian-advisory"] [data-document-role="heroVisual"] {
                    min-height: 520px;
                }

                [data-web-template="meridian-advisory"] [data-document-role="heroVisual"] .site-builder-image {
                    min-height: 520px !important;
                }

                [data-web-template="meridian-advisory"] [data-document-role="heroFloatingNote"] {
                    left: 1rem;
                }

                [data-web-template="meridian-advisory"] [data-document-role="heroVisual"]::before {
                    clip-path: polygon(0 0, 100% 0, 0 100%);
                    height: clamp(36px, 8vw, 60px);
                    left: 0;
                    top: -1px;
                    width: 100%;
                }

                [data-web-template="meridian-advisory"] [data-web-region="header"] {
                    min-height: 74px;
                    padding-inline: 1rem;
                }

                [data-web-template="meridian-advisory"] [data-web-region="header"] > .site-builder-grid {
                    grid-template-columns: minmax(0, 1fr) auto !important;
                }

                [data-web-template="meridian-advisory"] [data-web-region="header"] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="brand"]) {
                    display: block;
                    grid-column: 1 !important;
                    grid-row: 1 !important;
                }

                [data-web-template="meridian-advisory"] [data-web-region="header"] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="headerNavigation"]),
                [data-web-template="meridian-advisory"] [data-web-region="header"] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="headerActions"]) {
                    display: none;
                }

                [data-web-template="meridian-advisory"] [data-web-region="header"] .meridian-mobile-menu-toggle {
                    align-items: center;
                    background: var(--meridian-dark);
                    border: 1px solid var(--meridian-dark);
                    border-radius: 0;
                    cursor: pointer;
                    display: grid;
                    gap: 6px;
                    height: 44px;
                    justify-self: end;
                    padding: 10px;
                    place-content: center;
                    position: absolute;
                    right: 1rem;
                    top: 14px;
                    width: 44px;
                    z-index: 3;
                }

                [data-web-template="meridian-advisory"] [data-web-region="header"] .meridian-mobile-menu-toggle:focus-visible {
                    outline: 3px solid var(--site-accent);
                    outline-offset: 3px;
                }

                [data-web-template="meridian-advisory"] .meridian-mobile-menu-toggle > span {
                    background: var(--meridian-light);
                    display: block;
                    height: 2px;
                    transition: opacity 180ms ease, transform 180ms ease;
                    width: 23px;
                }

                [data-web-template="meridian-advisory"] [data-web-region="header"][data-meridian-mobile-menu-open] {
                    background: var(--meridian-paper) !important;
                    bottom: 0;
                    color: var(--meridian-ink);
                    left: 0;
                    overflow-y: auto;
                    position: fixed;
                    right: 0;
                    top: 0;
                    z-index: 90;
                }

                [data-web-template="meridian-advisory"] [data-web-region="header"][data-meridian-mobile-menu-open] > .site-builder-grid {
                    align-content: start;
                    min-height: 100%;
                    padding-bottom: 2rem;
                }

                [data-web-template="meridian-advisory"] [data-web-region="header"][data-meridian-mobile-menu-open] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="headerNavigation"]),
                [data-web-template="meridian-advisory"] [data-web-region="header"][data-meridian-mobile-menu-open] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="headerActions"]) {
                    display: block;
                    grid-column: 1 / -1 !important;
                }

                [data-web-template="meridian-advisory"] [data-web-region="header"][data-meridian-mobile-menu-open] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="headerNavigation"]) {
                    grid-row: 2 !important;
                    margin-top: clamp(3rem, 9vh, 5rem);
                }

                [data-web-template="meridian-advisory"] [data-web-region="header"][data-meridian-mobile-menu-open] > .site-builder-grid > .site-builder-grid-item:has(> [data-document-role="headerActions"]) {
                    grid-row: 3 !important;
                }

                [data-web-template="meridian-advisory"] [data-web-region="header"][data-meridian-mobile-menu-open] [data-document-role="headerNavigation"],
                [data-web-template="meridian-advisory"] [data-web-region="header"][data-meridian-mobile-menu-open] [data-document-role="headerActions"] {
                    align-items: stretch;
                    flex-direction: column;
                    gap: 0 !important;
                }

                [data-web-template="meridian-advisory"] [data-web-region="header"][data-meridian-mobile-menu-open] [data-document-role="headerNavigation"] > [data-site-builder-block="text"] {
                    border-bottom: 1px solid var(--site-border);
                    padding-block: 1rem;
                    width: 100%;
                }

                [data-web-template="meridian-advisory"] [data-web-region="header"][data-meridian-mobile-menu-open] [data-document-role="headerNavigation"] .site-builder-text {
                    display: block;
                    font-family: var(--site-heading-font) !important;
                    font-size: clamp(2rem, 9vw, 3.3rem) !important;
                    font-weight: 560 !important;
                    letter-spacing: -.035em !important;
                    width: 100% !important;
                }

                [data-web-template="meridian-advisory"] [data-web-region="header"][data-meridian-mobile-menu-open] [data-document-role="headerActions"] {
                    gap: .75rem !important;
                    margin-top: 1rem;
                }

                [data-web-template="meridian-advisory"] [data-web-region="header"][data-meridian-mobile-menu-open] [data-document-role="headerActions"] > *,
                [data-web-template="meridian-advisory"] [data-web-region="header"][data-meridian-mobile-menu-open] [data-document-role="headerCta"] {
                    width: 100% !important;
                }

                [data-web-template="meridian-advisory"] [data-web-region="header"][data-meridian-mobile-menu-open] .meridian-mobile-menu-toggle > span:first-child {
                    transform: translateY(8px) rotate(45deg);
                }

                [data-web-template="meridian-advisory"] [data-web-region="header"][data-meridian-mobile-menu-open] .meridian-mobile-menu-toggle > span:nth-child(2) {
                    opacity: 0;
                }

                [data-web-template="meridian-advisory"] [data-web-region="header"][data-meridian-mobile-menu-open] .meridian-mobile-menu-toggle > span:last-child {
                    transform: translateY(-8px) rotate(-45deg);
                }
            }

            @container (max-width: 640px) {
                [data-web-template="meridian-advisory"] .site-builder-section {
                    padding-inline: 1rem;
                }

                [data-web-template="meridian-advisory"] [data-web-region="homeProof"] > .site-builder-grid {
                    grid-template-columns: minmax(0, 1fr) !important;
                }

                [data-web-template="meridian-advisory"] [data-document-role="heroVisual"] {
                    min-height: 450px;
                }

                [data-web-template="meridian-advisory"] [data-document-role="heroVisual"] .site-builder-image {
                    min-height: 450px !important;
                }

                [data-web-template="meridian-advisory"] .site-builder-text-h1 {
                    font-size: clamp(3.35rem, 15vw, 5rem) !important;
                }

                [data-web-template="meridian-advisory"] .site-builder-text-h2 {
                    font-size: clamp(2.55rem, 11.5vw, 4rem) !important;
                }

                [data-web-template="meridian-advisory"] [data-document-role="heroPrimaryAction"],
                [data-web-template="meridian-advisory"] [data-document-role="heroSecondaryAction"] {
                    width: 100% !important;
                }
            }

            @media (prefers-reduced-motion: reduce) {
                [data-web-template="meridian-advisory"] *,
                [data-web-template="meridian-advisory"] *::before,
                [data-web-template="meridian-advisory"] *::after {
                    scroll-behavior: auto !important;
                    transition-duration: .01ms !important;
                }

                [data-web-template="meridian-advisory"] [data-meridian-reveal] {
                    opacity: 1 !important;
                    transform: none !important;
                }

                [data-web-template="meridian-advisory"] [data-document-role="heroVisual"] .site-builder-image,
                [data-web-template="meridian-advisory"] [data-document-role="editorialVisual"] .site-builder-image,
                [data-web-template="meridian-advisory"] [data-document-role="wideEditorialVisual"] .site-builder-image {
                    transform: none !important;
                }
            }
        `}</style>
    );
}

function MeridianAdvisoryFallbackPage({
    props,
    design,
    LinkComponent,
}: {
    props: TemplateRenderProps;
    design: ResolvedTemplateDesign;
    LinkComponent: TemplateLinkComponent;
}) {
    const page = props.page;

    if (!page) {
        return null;
    }

    return (
        <main className="px-5 py-20 sm:px-6" data-builder-document-region="page">
            <article className="mx-auto max-w-[900px] leading-8 text-[var(--site-muted)] [&_a]:text-[var(--site-primary)] [&_blockquote]:border-l-2 [&_blockquote]:border-[var(--site-accent)] [&_blockquote]:pl-5 [&_h1]:mb-7 [&_h1]:font-[var(--site-heading-font)] [&_h1]:text-6xl [&_h1]:font-medium [&_h1]:leading-[.94] [&_h1]:tracking-[-.045em] [&_h1]:text-[var(--site-text)] [&_h2]:mb-4 [&_h2]:mt-10 [&_h2]:font-[var(--site-heading-font)] [&_h2]:text-4xl [&_h2]:font-medium [&_h2]:text-[var(--site-text)] [&_li]:mb-2 [&_ol]:mb-6 [&_ol]:list-decimal [&_ol]:pl-6 [&_p]:mb-5 [&_strong]:text-[var(--site-text)] [&_ul]:mb-6 [&_ul]:list-disc [&_ul]:pl-6">
                <h1>{page.title}</h1>
                {renderTemplateContent(
                    page,
                    design,
                    LinkComponent,
                    props.leadForm,
                    props.readOnly,
                )}
            </article>
        </main>
    );
}

export const meridianAdvisoryTemplate: SiteTemplateDefinition = {
    manifest: {
        code: "meridian-advisory",
        name: "Advokát, účetní a poradce",
        type: "website",
        category: "professional",
        description: "Výrazná editoriální šablona pro advokáty, účetní a poradce s přesnou navigací, rozhodovacím rytmem a důvěryhodnou prezentací expertizy.",
        previewImage: "/templates/meridian-advisory/preview.jpg",
        defaults: webSystemTemplateDesignDefaults["meridian-advisory"],
        pageTypes: {
            home: { label: "Meridian homepage", slots: ["richContent"] },
            content: { label: "Meridian content page", slots: ["richContent"] },
            contact: { label: "Meridian contact page", slots: ["richContent"] },
        },
        editable: {
            colors: true,
            typography: true,
            radius: true,
            header: ["builder"],
            footer: ["builder"],
            buttons: ["solid", "outline"],
        },
    },
    render: (props: TemplateRenderProps) => {
        const design = mergeTemplateDesign(
            meridianAdvisoryTemplate.manifest.defaults,
            props.designOverride ?? props.site?.templateDesign,
        );
        const LinkComponent = props.LinkComponent ?? DefaultTemplateLink;

        return (
            <div
                className="min-h-screen overflow-x-hidden bg-[var(--site-background)] font-[var(--site-body-font)] text-[var(--site-text)] antialiased"
                data-web-template="meridian-advisory"
                data-web-template-editor={props.editorMode ? "true" : undefined}
                style={{
                    ...templateDesignToCssVars(design),
                    containerType: "inline-size",
                }}
            >
                <MeridianAdvisoryStyles />
                <WebGlobalBuilderRegion
                    design={design}
                    LinkComponent={LinkComponent}
                    props={props}
                    region="header"
                >
                    <MeridianAdvisoryMobileMenu
                        languageCode={props.page?.languageCode ?? props.site?.defaultLanguageCode}
                    />
                </WebGlobalBuilderRegion>
                <WebBuilderPageRegion
                    design={design}
                    fallback={(
                        <MeridianAdvisoryFallbackPage
                            design={design}
                            LinkComponent={LinkComponent}
                            props={props}
                        />
                    )}
                    LinkComponent={LinkComponent}
                    props={props}
                />
                <WebGlobalBuilderRegion
                    design={design}
                    LinkComponent={LinkComponent}
                    props={props}
                    region="footer"
                />
            </div>
        );
    },
};
